import { DOMParser, XMLSerializer } from "@xmldom/xmldom";
import * as xpath from "xpath";
import { WordFragment } from "../create";
import {
    CreateImage,
    CreateReference,
    CreateSection,
    Relationship,
    ListNested,
    OptionsProperties,
    OptionsReplaceVariableImage,
    OptionsReplaceVariableList,
    OptionsReplaceVariableTable,
    OptionsReplaceVariableWordFragment,
    OptionsSectionLayout,
    Target,
} from "../element";
import { numberingXml, relationshipXml, settingsTagsOrder } from "./ooxml";
import {
    applyHtmlSpecialChars,
    escapeRegExp,
    generateRandomNumber,
    generateRegExpForReplacement,
    generateUniqid,
    getNamespace,
} from "../util";

type ContentType =
    | Target
    | "app"
    | "core"
    | "numbering"
    | "settings"
    | "styles";

/**
 * Docx structure
 */
class Docx {
    /**
     * Conformance
     */
    protected conformance: "transitional" | "strict";

    /**
     * File contents
     */
    protected fileContents: Map<string, ArrayBuffer>;

    /**
     * DOCX namespaces
     */
    protected namespaces: Map<string, string>;

    /**
     * Text contents
     */
    protected textContents: Map<string, string>;

    /**
     * Constructor
     */
    constructor() {
        this.conformance = "transitional";
        this.fileContents = new Map<string, ArrayBuffer>();
        this.namespaces = new Map<string, string>();
        this.textContents = new Map<string, string>();
    }

    /**
     * Getter file contents
     *
     * @returns File contents
     */
    public getFileContents(): Map<string, ArrayBuffer> {
        return this.fileContents;
    }

    /**
     * Getter namespaces
     *
     * @returns Namespaces
     */
    public getNamespaces(): Map<string, string> {
        return this.namespaces;
    }

    /**
     * Getter text contents
     *
     * @returns Text contents
     */
    public getTextContents(): Map<string, string> {
        return this.textContents;
    }

    /**
     * Adds a file content
     *
     * @param path - File path
     * @param content - File content
     */
    public addFileContent(path: string, content: ArrayBuffer) {
        this.fileContents.set(path, content);
    }

    /**
     * Adds a text content
     *
     * @param path - File path
     * @param content - File content
     */
    public addTextContent(path: string, content: string) {
        this.textContents.set(path, content);
    }

    /**
     * Adds XML content in document
     *
     * @param xml - XML content
     */
    public addXmlContent(xml: string) {
        const targetContentsPath = this.getContentPathsByType("document");
        let nodeXpathQuery = "//w:document";
        const insertMode = "insertBefore";

        if (targetContentsPath != null && targetContentsPath.length > 0) {
            targetContentsPath.forEach(targetContentPath => {
                const targetKey = targetContentPath;
                const targetContent = this.getTextContent(targetKey);
                if (targetContent != null) {
                    nodeXpathQuery = "(//w:sectPr)[last()]";

                    const contentDom = new DOMParser().parseFromString(
                        targetContent,
                        "text/xml"
                    );

                    const selectXpath = xpath.useNamespaces({
                        w: getNamespace(this.namespaces, "w"),
                    });
                    const nodesXpath = selectXpath(
                        nodeXpathQuery,
                        contentDom
                    ) as Element[];

                    if (nodesXpath.length > 0) {
                        // wrap the XML contents in a w:root tag to add all child nodes
                        const xmlDom = new DOMParser().parseFromString(
                            "<w:root>" + xml + "</w:root>",
                            "text/xml"
                        );
                        const childNodes = xmlDom.documentElement.childNodes;
                        Array.from(childNodes).forEach(childNode => {
                            // clone the node to be inserted to avoid removing it from childNodes
                            const newChildNode = childNode.cloneNode(true);
                            if (insertMode == "insertBefore") {
                                // insert before insertion
                                const nodeXpathParent =
                                    nodesXpath[0].parentNode;
                                if (nodeXpathParent != null) {
                                    nodeXpathParent.insertBefore(
                                        newChildNode,
                                        nodesXpath[0]
                                    );
                                }
                            } else if (insertMode == "appendChild") {
                                // append child insertion
                                nodesXpath[0].appendChild(newChildNode);
                            }
                        });

                        this.addTextContent(
                            targetKey,
                            new XMLSerializer().serializeToString(contentDom)
                        );
                    }
                }
            });
        }
    }

    /**
     * Adds XML reference (header, footer)
     *
     * @param reference - Reference element
     */
    public addXmlContentReference(reference: CreateReference) {
        const targetContentsPath = this.getContentPathsByType("document");

        // get target section of the new reference
        let nodeXpathQuerySections = "(//w:sectPr)[last()]";
        if (reference.getOptions().section != null) {
            if (
                typeof reference.getOptions().section === "number" &&
                reference.getOptions().section != -1
            ) {
                nodeXpathQuerySections =
                    "(//w:sectPr)[" + reference.getOptions().section + "]";
            } else if (
                typeof reference.getOptions().section === "string" &&
                reference.getOptions().section == "all"
            ) {
                nodeXpathQuerySections = "//w:sectPr";
            }
        }

        if (targetContentsPath != null && targetContentsPath.length > 0) {
            targetContentsPath.forEach(targetContentPath => {
                const targetKey = targetContentPath;
                const targetContent = this.getTextContent(targetKey);
                if (targetContent != null) {
                    const contentDom = new DOMParser().parseFromString(
                        targetContent,
                        "text/xml"
                    );

                    const selectXpath = xpath.useNamespaces({
                        w: getNamespace(this.namespaces, "w"),
                    });
                    const nodesXpathSections = selectXpath(
                        nodeXpathQuerySections,
                        contentDom
                    ) as Element[];
                    nodesXpathSections.forEach(nodesXpathSection => {
                        const typeReferences =
                            nodesXpathSection.getElementsByTagNameNS(
                                getNamespace(this.namespaces, "w"),
                                reference.getTarget() + "Reference"
                            );
                        // check if the reference type exists
                        let referenceContent;
                        let referenceTarget;
                        Array.from(typeReferences).forEach(typeReference => {
                            if (
                                typeReference.getAttribute("w:type") ==
                                reference.getOptions().type
                            ) {
                                const rIdReference =
                                    typeReference.getAttribute("r:id");
                                if (rIdReference != null) {
                                    const targetContentsPathRels =
                                        this.getContentPathsByType(
                                            "document",
                                            true
                                        );
                                    if (
                                        targetContentsPathRels != null &&
                                        targetContentsPathRels.length > 0
                                    ) {
                                        const targetKeyRels =
                                            targetContentsPathRels[0]; // get only the first target. Relationships only use one internal file
                                        const targetContentRels =
                                            this.getTextContent(targetKeyRels);
                                        if (targetContentRels != null) {
                                            const xmlDomRels =
                                                new DOMParser().parseFromString(
                                                    targetContentRels,
                                                    "text/xml"
                                                );
                                            const selectXpath =
                                                xpath.useNamespaces({
                                                    rels: getNamespace(
                                                        this.namespaces,
                                                        "rels"
                                                    ),
                                                });

                                            const nodesXpathRels = selectXpath(
                                                '//rels:Relationship[@Id="' +
                                                    rIdReference +
                                                    '"]',
                                                xmlDomRels
                                            ) as Element[];
                                            if (nodesXpathRels.length > 0) {
                                                referenceTarget =
                                                    "word/" +
                                                    nodesXpathRels[0].getAttribute(
                                                        "Target"
                                                    );
                                                referenceContent =
                                                    this.getTextContent(
                                                        referenceTarget
                                                    );
                                            }
                                        }
                                    }
                                }
                            }
                        });
                        if (referenceContent == null) {
                            // a reference content doesn't exist, generate it

                            const referenceId = generateUniqid("");
                            referenceTarget =
                                reference.getTarget() + referenceId + ".xml";

                            // add relationship
                            const newRelationship: Relationship = {
                                id: "rId" + referenceId,
                                target: referenceTarget,
                                type:
                                    "http://schemas.openxmlformats.org/officeDocument/2006/relationships/" +
                                    reference.getTarget(),
                            };
                            this.addXmlRelationship(
                                newRelationship,
                                "document"
                            );

                            // set the internal path to add the new contents
                            referenceTarget = "word/" + referenceTarget;

                            // add the new reference into the section
                            const contentDomNewReference =
                                new DOMParser().parseFromString(
                                    "<w:" +
                                        reference.getTarget() +
                                        'Reference r:id="' +
                                        "rId" +
                                        referenceId +
                                        '" w:type="' +
                                        reference.getOptions().type +
                                        '"/>',
                                    "text/xml"
                                );
                            const newReferenceNode =
                                contentDomNewReference.documentElement;
                            if (newReferenceNode != null) {
                                nodesXpathSection.insertBefore(
                                    newReferenceNode,
                                    nodesXpathSection.firstChild
                                );
                            }

                            // add the new Override
                            this.addXmlOverride(
                                "/" + referenceTarget,
                                "application/vnd.openxmlformats-officedocument.wordprocessingml." +
                                    reference.getTarget() +
                                    "+xml"
                            );

                            // create reference contents
                            referenceContent = reference.create();
                        } else {
                            // the reference exists, check if it must be replaced
                            if (
                                reference.getOptions().replaceOthers != null &&
                                !reference.getOptions().replaceOthers
                            ) {
                                referenceContent = null;
                            } else {
                                // create reference contents
                                referenceContent = reference.create();
                            }
                        }

                        if (
                            referenceContent != null &&
                            referenceTarget != null
                        ) {
                            if (
                                reference.getOptions().type != null &&
                                reference.getOptions().type == "first"
                            ) {
                                const nodesTitlePg =
                                    nodesXpathSection.getElementsByTagNameNS(
                                        getNamespace(this.namespaces, "w"),
                                        "titlePg"
                                    );
                                if (nodesTitlePg.length == 0) {
                                    const contentDomTitlePg =
                                        new DOMParser().parseFromString(
                                            "<w:titlePg/>",
                                            "text/xml"
                                        );
                                    const newTitlePgNode =
                                        contentDomTitlePg.documentElement;
                                    if (newTitlePgNode != null) {
                                        nodesXpathSection.appendChild(
                                            newTitlePgNode
                                        );
                                    }
                                } else {
                                    nodesTitlePg
                                        .item(0)
                                        ?.setAttribute("w:val", "true");
                                }
                            }

                            this.addTextContent(
                                referenceTarget,
                                referenceContent
                            );
                        }
                    });

                    this.addTextContent(
                        targetKey,
                        new XMLSerializer().serializeToString(contentDom)
                    );
                }
            });
        }
    }

    /**
     * Adds DOCX default
     *
     * @param extension - File extension
     * @param contentType - Content type
     */
    public addXmlDefault(extension: string, contentType: string) {
        const contentTypesContent = this.getTextContent("[Content_Types].xml");

        if (contentTypesContent != null) {
            const contentTypesDom = new DOMParser().parseFromString(
                contentTypesContent,
                "text/xml"
            );
            const selectXpath = xpath.useNamespaces({
                xmlns: "http://schemas.openxmlformats.org/package/2006/content-types",
            });
            // check lower and upper cases. Extensions can't be repeated
            const nodesXpathLowerCase = selectXpath(
                "//xmlns:Default[@Extension='" + extension.toLowerCase() + "']",
                contentTypesDom
            ) as Element[];
            const nodesXpathUpperCase = selectXpath(
                "//xmlns:Default[@Extension='" + extension.toUpperCase() + "']",
                contentTypesDom
            ) as Element[];
            // add the new default only if the extension doesn't exist
            if (
                nodesXpathLowerCase.length == 0 &&
                nodesXpathUpperCase.length == 0
            ) {
                const contentDom = new DOMParser().parseFromString(
                    '<Default ContentType="' +
                        contentType +
                        '" Extension="' +
                        extension +
                        '"/>',
                    "text/xml"
                );
                const newDefaultNode = contentDom.documentElement;
                if (newDefaultNode != null) {
                    contentTypesDom.documentElement.appendChild(newDefaultNode);
                }

                this.addTextContent(
                    "[Content_Types].xml",
                    new XMLSerializer().serializeToString(contentTypesDom)
                );
            }
        }
    }

    /**
     * Adds XML numbering
     *
     * @param abstractNum - Abstract num
     * @param num - Num content
     */
    public addXmlNumbering(abstractNum: string, num: string) {
        // check if there's a numbering.xml style.
        // If this numbering file doesn't exist add it from a skeleton
        let contentsNumbering = this.getContentPathsByType("numbering");
        if (contentsNumbering == null) {
            this.addXmlSkeletonFile("numbering");
            // get the numbering content after the skeleton has been added
            contentsNumbering = this.getContentPathsByType("numbering");

            // add relationship
            const newRelationship: Relationship = {
                id: generateUniqid("rId"),
                target: "numbering.xml",
                type: "http://schemas.openxmlformats.org/officeDocument/2006/relationships/numbering",
            };
            this.addXmlRelationship(newRelationship, "document");
        }

        if (contentsNumbering != null) {
            const targetKey = contentsNumbering[0]; // get only the first target. Settings only has one internal file
            const targetContent = this.getTextContent(targetKey);

            if (targetContent != null) {
                const contentNumberingDom = new DOMParser().parseFromString(
                    targetContent,
                    "text/xml"
                );

                // add the new numbering that includes two elements: abstractNum and num

                // add abstractNum
                const contentDom = new DOMParser().parseFromString(
                    abstractNum,
                    "text/xml"
                );
                const newDefaultNode = contentDom.documentElement;
                if (newDefaultNode != null) {
                    if (
                        contentNumberingDom.documentElement.firstChild == null
                    ) {
                        contentNumberingDom.documentElement.appendChild(
                            newDefaultNode
                        );
                    } else {
                        contentNumberingDom.documentElement.firstChild.parentNode?.insertBefore(
                            newDefaultNode,
                            contentNumberingDom.documentElement.firstChild
                        );
                    }
                }
                let numberingContents = new XMLSerializer().serializeToString(
                    contentNumberingDom
                );

                // add num
                numberingContents = numberingContents.replace(
                    "</w:numbering>",
                    num + "</w:numbering>"
                );

                this.addTextContent(targetKey, numberingContents);
            }
        }
    }

    /**
     * Adds DOCX override
     *
     * @param partName - Part name
     * @param contentType - Content type
     */
    public addXmlOverride(partName: string, contentType: string) {
        const contentTypesContent = this.getTextContent("[Content_Types].xml");

        if (contentTypesContent != null) {
            const contentTypesDom = new DOMParser().parseFromString(
                contentTypesContent,
                "text/xml"
            );
            const selectXpath = xpath.useNamespaces({
                xmlns: "http://schemas.openxmlformats.org/package/2006/content-types",
            });
            // partName can't be repeated
            const nodesXpathPartName = selectXpath(
                "//xmlns:Override[@PartName='" + partName + "']",
                contentTypesDom
            ) as Element[];
            // add the new default only if the partName doesn't exist
            if (nodesXpathPartName.length == 0) {
                const contentDom = new DOMParser().parseFromString(
                    '<Override ContentType="' +
                        contentType +
                        '" PartName="' +
                        partName +
                        '"/>',
                    "text/xml"
                );
                const newDefaultNode = contentDom.documentElement;
                if (newDefaultNode != null) {
                    contentTypesDom.documentElement.appendChild(newDefaultNode);
                }

                this.addTextContent(
                    "[Content_Types].xml",
                    new XMLSerializer().serializeToString(contentTypesDom)
                );
            }
        }
    }

    /**
     * Adds DOCX properties
     *
     * @param settings - Properties options
     */
    public addXmlProperties(options: OptionsProperties) {
        const targetContentsPathApp = this.getContentPathsByType("app");
        const targetContentsPathCore = this.getContentPathsByType("core");
        const addProperty = (
            dom: Document,
            tag: string,
            value: string,
            attributes?: Map<string, string>
        ) => {
            const nodesProperty = dom.getElementsByTagName(tag);
            const nodeProperty = nodesProperty.item(0);
            if (nodeProperty != null) {
                nodeProperty.textContent = value;
            } else {
                const newNode = dom.createElement(tag);
                if (attributes != null) {
                    for (const [
                        attributeName,
                        attributeValue,
                    ] of attributes.entries()) {
                        newNode.setAttribute(attributeName, attributeValue);
                    }
                }
                newNode.textContent = value;

                dom.documentElement.appendChild(newNode);
            }
        };

        if (targetContentsPathApp != null && targetContentsPathApp.length > 0) {
            const targetKey = targetContentsPathApp[0]; // get only the first target. app only has one internal file
            const targetContent = this.getTextContent(targetKey);
            if (targetContent != null) {
                const xmlDom = new DOMParser().parseFromString(
                    targetContent,
                    "text/xml"
                );

                if (options.manager != null) {
                    addProperty(xmlDom, "Manager", options.manager);
                }

                if (options.company != null) {
                    addProperty(xmlDom, "Company", options.company);
                }

                this.addTextContent(
                    targetKey,
                    new XMLSerializer().serializeToString(xmlDom)
                );
            }
        }

        if (
            targetContentsPathCore != null &&
            targetContentsPathCore.length > 0
        ) {
            const targetKey = targetContentsPathCore[0]; // get only the first target. app only has one internal file
            const targetContent = this.getTextContent(targetKey);
            if (targetContent != null) {
                const xmlDom = new DOMParser().parseFromString(
                    targetContent,
                    "text/xml"
                );

                if (options.category != null) {
                    const attributes = new Map<string, string>();
                    attributes.set(
                        "xmlns:cp",
                        "http://schemas.openxmlformats.org/package/2006/metadata/core-properties"
                    );
                    addProperty(
                        xmlDom,
                        "cp:category",
                        options.category,
                        attributes
                    );
                }

                if (options.created != null) {
                    const attributes = new Map<string, string>();
                    attributes.set(
                        "xmlns:dcterms",
                        "http://purl.org/dc/terms/"
                    );
                    attributes.set("xsi:type", "dcterms:W3CDTF");
                    addProperty(
                        xmlDom,
                        "dcterms:created",
                        options.created,
                        attributes
                    );
                }

                if (options.creator != null) {
                    const attributes = new Map<string, string>();
                    attributes.set(
                        "xmlns:dc",
                        "http://purl.org/dc/elements/1.1/"
                    );
                    addProperty(xmlDom, "dc:creator", options.creator);
                }

                if (options.description != null) {
                    const attributes = new Map<string, string>();
                    attributes.set(
                        "xmlns:dc",
                        "http://purl.org/dc/elements/1.1/"
                    );
                    addProperty(
                        xmlDom,
                        "dc:description",
                        options.description,
                        attributes
                    );
                }

                if (options.keywords != null) {
                    const attributes = new Map<string, string>();
                    attributes.set(
                        "xmlns:cp",
                        "http://schemas.openxmlformats.org/package/2006/metadata/core-properties"
                    );
                    addProperty(
                        xmlDom,
                        "cp:keywords",
                        options.keywords,
                        attributes
                    );
                }

                if (options.lastModifiedBy != null) {
                    const attributes = new Map<string, string>();
                    attributes.set(
                        "xmlns:cp",
                        "http://schemas.openxmlformats.org/package/2006/metadata/core-properties"
                    );
                    addProperty(
                        xmlDom,
                        "cp:lastModifiedBy",
                        options.lastModifiedBy,
                        attributes
                    );
                }

                if (options.modified != null) {
                    const attributes = new Map<string, string>();
                    attributes.set(
                        "xmlns:dcterms",
                        "http://purl.org/dc/terms/"
                    );
                    attributes.set("xsi:type", "dcterms:W3CDTF");
                    addProperty(
                        xmlDom,
                        "dcterms:modified",
                        options.modified,
                        attributes
                    );
                }

                if (options.revision != null) {
                    const attributes = new Map<string, string>();
                    attributes.set(
                        "xmlns:cp",
                        "http://schemas.openxmlformats.org/package/2006/metadata/core-properties"
                    );
                    addProperty(
                        xmlDom,
                        "cp:revision",
                        options.revision,
                        attributes
                    );
                }

                if (options.subject != null) {
                    const attributes = new Map<string, string>();
                    attributes.set(
                        "xmlns:dc",
                        "http://purl.org/dc/elements/1.1/"
                    );
                    addProperty(
                        xmlDom,
                        "dc:subject",
                        options.subject,
                        attributes
                    );
                }

                if (options.title != null) {
                    const attributes = new Map<string, string>();
                    attributes.set(
                        "xmlns:dc",
                        "http://purl.org/dc/elements/1.1/"
                    );
                    addProperty(xmlDom, "dc:title", options.title, attributes);
                }

                this.addTextContent(
                    targetKey,
                    new XMLSerializer().serializeToString(xmlDom)
                );
            }
        }
    }

    /**
     * Adds XML relationship
     *
     * @param xml - XML content
     * @param target - Target file
     */
    public addXmlRelationship(
        relationship: Relationship,
        target: Target = "document"
    ) {
        // generate new relationship
        let newRelationship =
            '<Relationship Id="' +
            relationship.id +
            '" Target="' +
            relationship.target +
            '" ';
        if (relationship.targetMode != null) {
            newRelationship += ' TargetMode="' + relationship.targetMode + '" ';
        }
        newRelationship += ' Type="' + relationship.type + '"/>';

        const targetContentsPath = this.getContentPathsByType(target, true);

        if (targetContentsPath != null && targetContentsPath.length > 0) {
            targetContentsPath.forEach(targetContentPath => {
                const targetContent = this.getTextContent(targetContentPath);
                if (targetContent != null) {
                    const xmlDom = new DOMParser().parseFromString(
                        targetContent,
                        "text/xml"
                    );
                    const selectXpath = xpath.useNamespaces({
                        rels: getNamespace(this.namespaces, "rels"),
                    });

                    // check if the id exists.
                    // Do not add it in this case to avoid duplicated relationships
                    const nodesXpath = selectXpath(
                        '//rels:Relationship[@Id="' + relationship.id + '"]',
                        xmlDom
                    ) as Element[];

                    if (nodesXpath.length == 0) {
                        const contentDom = new DOMParser().parseFromString(
                            newRelationship,
                            "text/xml"
                        );

                        const newRelationshipNode = contentDom.documentElement;
                        xmlDom.documentElement.appendChild(newRelationshipNode);

                        this.addTextContent(
                            targetContentPath,
                            new XMLSerializer().serializeToString(xmlDom)
                        );
                    }
                }
            });
        }
    }

    /**
     * Adds DOCX settings
     *
     * @param settings - Settings options
     */
    public addXmlSettings(settings: Map<string, string>) {
        const targetContentsPath = this.getContentPathsByType("settings");

        if (targetContentsPath != null && targetContentsPath.length > 0) {
            const targetKey = targetContentsPath[0]; // get only the first target. Settings only has one internal file
            const targetContent = this.getTextContent(targetKey);
            if (targetContent != null) {
                const xmlDom = new DOMParser().parseFromString(
                    targetContent,
                    "text/xml"
                );

                // insert new settings
                for (const [settingName, settingValue] of settings.entries()) {
                    const nodeXpathQuery = "//" + settingName;
                    const selectXpath = xpath.useNamespaces({
                        w: getNamespace(this.namespaces, "w"),
                    });
                    const nodesXpath = selectXpath(
                        nodeXpathQuery,
                        xmlDom
                    ) as Element[];

                    const contentDom = new DOMParser().parseFromString(
                        settingValue,
                        "text/xml"
                    );

                    if (nodesXpath.length > 0) {
                        // the setting exists, add the new setting and remove the previous one
                        const nodeParent = nodesXpath[0].parentNode;
                        if (nodeParent != null) {
                            const newSettingNode = contentDom.documentElement;
                            if (newSettingNode != null) {
                                nodeParent.insertBefore(
                                    newSettingNode,
                                    nodesXpath[0]
                                );
                                nodeParent.removeChild(nodesXpath[0]);
                            }
                        }
                    } else {
                        // the setting doesn't exist, add it in the correct order
                        const childNodesSettings =
                            xmlDom.documentElement.childNodes;
                        const indexNewSetting =
                            settingsTagsOrder.indexOf(settingName);
                        let settingAdded = false;
                        Array.from(childNodesSettings).forEach(
                            childNodeSettings => {
                                const indexExistingSetting =
                                    settingsTagsOrder.indexOf(
                                        childNodeSettings.nodeName
                                    );
                                if (indexExistingSetting > indexNewSetting) {
                                    const nodeParent =
                                        childNodeSettings.parentNode;
                                    if (nodeParent != null) {
                                        const newSettingNode =
                                            contentDom.documentElement;
                                        if (newSettingNode != null) {
                                            nodeParent.insertBefore(
                                                newSettingNode,
                                                childNodeSettings
                                            );
                                            settingAdded = true;
                                        }
                                    }
                                }
                            }
                        );
                        // in case no node was found (pretty unlikely) append the node
                        const newSettingNode = contentDom.documentElement;
                        if (!settingAdded && newSettingNode != null) {
                            xmlDom.documentElement.appendChild(newSettingNode);
                        }
                    }
                }

                this.addTextContent(
                    targetKey,
                    new XMLSerializer().serializeToString(xmlDom)
                );
            }
        }
    }

    /**
     * Add XML skeleton
     *
     * @param contentType - Content type
     */
    public addXmlSkeletonFile(contentType: ContentType) {
        if (contentType == "numbering") {
            this.addTextContent("word/numbering.xml", numberingXml);
            this.addXmlOverride(
                "/word/numbering.xml",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.numbering+xml"
            );
        }
    }

    /**
     * Adds DOCX custom style
     *
     * @param style - Custom style contents
     * @param type - Style type
     * @param name - Style name
     */
    public addXmlStyleCustom(
        style: string,
        type: "character" | "paragraph",
        name: string
    ) {
        // create the XML style
        let newStyleXml;

        if (type == "character") {
            newStyleXml =
                '<w:style w:customStyle="1" w:styleId="' +
                name +
                '" w:type="character"><w:name w:val="' +
                name +
                '"/><w:link w:val="' +
                name +
                '"/><w:uiPriority w:val="99"/><w:semiHidden/><w:unhideWhenUsed/><w:rsid w:val="006E0FDA"/>' +
                style +
                "</w:style>";
        } else if (type == "paragraph") {
            // get pPr and rPr styles as separated contents to be added in the new style
            const pPrStyle =
                style.substring(
                    style.indexOf("<w:pPr>"),
                    style.indexOf("<w:rPr>")
                ) + "</w:pPr>";
            const rPrStyle =
                style.substring(
                    style.indexOf("<w:rPr>"),
                    style.indexOf("</w:rPr>")
                ) + "</w:rPr>";
            newStyleXml =
                '<w:style w:customStyle="1" w:styleId="' +
                name +
                '" w:type="paragraph"><w:name w:val="' +
                name +
                '"/><w:link w:val="' +
                name +
                'Car"/><w:uiPriority w:val="99"/><w:semiHidden/><w:unhideWhenUsed/><w:rsid w:val="006E0FDA"/>' +
                pPrStyle +
                rPrStyle +
                '</w:style><w:style w:customStyle="1" w:styleId="' +
                name +
                'Car" w:type="character"><w:name w:val="' +
                name +
                '"/><w:link w:val="' +
                name +
                '"/><w:uiPriority w:val="99"/><w:semiHidden/><w:unhideWhenUsed/><w:rsid w:val="006E0FDA"/>' +
                rPrStyle +
                "</w:style>";
        }

        if (newStyleXml != null) {
            this.addXmlStyle(newStyleXml);
        }
    }

    /**
     * Adds XML style
     *
     * @param xml - Styles
     */
    public addXmlStyle(xml: string) {
        const targetContentsPath = this.getContentPathsByType("styles");

        if (targetContentsPath != null && targetContentsPath.length > 0) {
            const targetKey = targetContentsPath[0]; // get only the first target. Styles only has one internal file
            const targetContent = this.getTextContent(targetKey);
            if (targetContent != null) {
                const styleContents = targetContent.replace(
                    "</w:styles>",
                    xml + "</w:styles>"
                );

                this.addTextContent(targetKey, styleContents);
            }
        }
    }

    /**
     * Extracts XML variables
     *
     * @param content - XML content
     * @param templateSymbolStart - Template symbol start
     * @param templateSymbolEnd - Template symbol end
     * @param regExprVariableSymbols - Regular expression variable symbols
     * @returns Variables
     */
    public extractXmlVariables(
        content: string,
        templateSymbolStart: string,
        templateSymbolEnd: string,
        regExprVariableSymbols: string
    ): string[] {
        const variables: string[] = [];
        const contentDom = new DOMParser().parseFromString(content, "text/xml");
        const selectXpath = xpath.useNamespaces({
            w: getNamespace(this.namespaces, "w"),
            wp: getNamespace(this.namespaces, "wp"),
        });
        // extract variables
        const matchPlaceholderExtract = (
            content: string,
            variables: string[]
        ) => {
            const matchesPlaceholders = content.match(
                new RegExp(
                    escapeRegExp(templateSymbolStart) +
                        regExprVariableSymbols +
                        escapeRegExp(templateSymbolEnd),
                    "g"
                )
            );

            if (matchesPlaceholders != null) {
                matchesPlaceholders.forEach(matchPlaceholders => {
                    // keep the placeholder removing template symbols
                    variables.push(
                        matchPlaceholders
                            .replace(templateSymbolStart, "")
                            .replace(templateSymbolEnd, "")
                    );
                });
            }
        };

        // text nodes
        const nodesXpathT = selectXpath("//w:t", contentDom) as Element[];
        nodesXpathT.forEach(nodeXpathT => {
            matchPlaceholderExtract(nodeXpathT.toString(), variables);
        });

        // image nodes
        const nodesXpathDocPr = selectXpath(
            "//wp:docPr",
            contentDom
        ) as Element[];
        nodesXpathDocPr.forEach(nodeXpathDocPr => {
            const titleAttribute = nodeXpathDocPr.getAttribute("title");
            if (titleAttribute != null) {
                matchPlaceholderExtract(titleAttribute, variables);
            }

            const descrAttribute = nodeXpathDocPr.getAttribute("descr");
            if (descrAttribute != null) {
                matchPlaceholderExtract(descrAttribute, variables);
            }
        });

        return variables;
    }

    /**
     *  Generates inline contents
     *
     * @param xml - XML content
     * @returns Inline XML contents
     */
    public getInlineXmlContents(xml: string): string {
        let inlineXmlContents = "";

        // get only inline contents
        const wordFragmentDom = new DOMParser().parseFromString(
            '<w:root xmlns:ve="http://schemas.openxmlformats.org/markup-compatibility/2006" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" xmlns:m="http://schemas.openxmlformats.org/officeDocument/2006/math" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing" xmlns:w10="urn:schemas-microsoft-com:office:word" xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" xmlns:wne="http://schemas.microsoft.com/office/word/2006/wordml">' +
                xml +
                "</w:root>",
            "text/xml"
        );

        const selectXpath = xpath.useNamespaces({
            m: getNamespace(this.namespaces, "m"),
            w: getNamespace(this.namespaces, "w"),
        });
        const nodesXpath = selectXpath(
            "//w:r[not(ancestor::w:hyperlink or ancestor::w:fldSimple or ancestor::w:ins or ancestor::w:sdt)] | //w:hyperlink | //w:bookmarkStart | //w:bookmarkEnd | //w:commentRangeStart | //w:commentRangeEnd | //m:oMath | //w:fldSimple | //w:ins | //w:sdt[not(.//w:sdt)]",
            wordFragmentDom
        ) as Element[];
        nodesXpath.forEach(nodeXpath => {
            inlineXmlContents += nodeXpath.toString();
        });

        return inlineXmlContents;
    }

    /**
     * Gets XML contents
     *
     * @param xml - XML content
     * @param xpathQuery - XPath query
     * @returns XML contents
     */
    public getXmlContents(xml: string, xpathQuery: string): string {
        let xmlContents = "";

        const xmlDom = new DOMParser().parseFromString(xml, "text/xml");

        const selectXpath = xpath.useNamespaces({
            m: getNamespace(this.namespaces, "m"),
            w: getNamespace(this.namespaces, "w"),
        });
        const nodesXpath = selectXpath(xpathQuery, xmlDom) as Element[];
        nodesXpath.forEach(nodeXpath => {
            xmlContents += nodeXpath.toString();
        });

        return xmlContents;
    }

    /**
     * Gets text content by type
     *
     * @param contentType - Content type
     * @param rels - Get rels file
     * @returns Contents
     */
    public getContentPathsByType(
        contentType: ContentType,
        rels: boolean = false
    ): string[] | null {
        const contentTypePaths: string[] = [];
        const contentTypesContent = this.getTextContent("[Content_Types].xml");

        if (contentTypesContent == null) {
            return null;
        }

        const contentTypesDom = new DOMParser().parseFromString(
            contentTypesContent,
            "text/xml"
        );

        if (rels) {
            contentType += ".rels";
        }

        if (contentTypesDom != null) {
            // get Override type
            let overrideContentType = "";
            if (contentType.startsWith("app")) {
                overrideContentType =
                    "application/vnd.openxmlformats-officedocument.extended-properties+xml";
            } else if (contentType.startsWith("comment")) {
                overrideContentType =
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.comments+xml";
            } else if (contentType.startsWith("core")) {
                overrideContentType =
                    "application/vnd.openxmlformats-package.core-properties+xml";
            } else if (contentType.startsWith("document")) {
                overrideContentType =
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml";
            } else if (contentType.startsWith("endnote")) {
                overrideContentType =
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.endnotes+xml";
            } else if (contentType.startsWith("footer")) {
                overrideContentType =
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.footer+xml";
            } else if (contentType.startsWith("footnote")) {
                overrideContentType =
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.footnotes+xml";
            } else if (contentType.startsWith("header")) {
                overrideContentType =
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.header+xml";
            } else if (contentType.startsWith("numbering")) {
                overrideContentType =
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.numbering+xml";
            } else if (contentType.startsWith("settings")) {
                overrideContentType =
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.settings+xml";
            } else if (contentType.startsWith("styles")) {
                overrideContentType =
                    "application/vnd.openxmlformats-officedocument.wordprocessingml.styles+xml";
            }

            const selectXpath = xpath.useNamespaces({
                xmlns: "http://schemas.openxmlformats.org/package/2006/content-types",
            });
            const nodesXpath = selectXpath(
                "//xmlns:Override[@ContentType='" + overrideContentType + "']",
                contentTypesDom
            ) as Element[];

            if (nodesXpath.length > 0) {
                // iterate nodes to get all contents with the chosen content type
                nodesXpath.forEach(nodeXpath => {
                    const filePath = nodeXpath.getAttribute("PartName");
                    if (filePath != null) {
                        // remove first character (/) from the attribute value to get the correct path
                        let internalFilePath = filePath.slice(1);
                        if (rels) {
                            // get rels file
                            internalFilePath += ".rels";
                            internalFilePath = internalFilePath.replace(
                                "word/",
                                "word/_rels/"
                            );

                            const contentTypeContents =
                                this.getTextContent(internalFilePath);
                            if (rels && contentTypeContents == null) {
                                // rels file doesn't exist, generate and add a new one
                                this.addTextContent(
                                    internalFilePath,
                                    relationshipXml
                                );
                            }
                        }
                        contentTypePaths.push(internalFilePath);
                    }
                });
            }
        }

        return contentTypePaths.length > 0 ? contentTypePaths : null;
    }

    /**
     * Gets a file content
     *
     * @param path - File path
     * @returns File contents
     */
    public getFileContent(path: string): ArrayBuffer | null {
        const fileContents = this.fileContents.get(path);

        return fileContents ?? null;
    }

    /**
     * Gets a text content
     *
     * @param path - File path
     * @returns File contents
     */
    public getTextContent(path: string): string | null {
        const textContents = this.textContents.get(path);

        return textContents ?? null;
    }

    /**
     * Imports MS Word default styles
     *
     * @param docxDefaultStructure - DOCX structure with the styles to be imported
     */
    public importMSWordDefaultStyles(docxDefaultStructure: Docx) {
        // use DOCX default structre to get MS Word default styles
        const targetDefaultContent =
            docxDefaultStructure.getTextContent("word/styles.xml");

        // get styles from the current DOCX structure to check if styles exist
        let targetContent;
        const targetContentsPath = this.getContentPathsByType("styles");
        if (targetContentsPath != null && targetContentsPath.length > 0) {
            const targetKey = targetContentsPath[0]; // get only the first target. Styles only has one internal file
            targetContent = this.getTextContent(targetKey);
        }

        if (targetDefaultContent != null && targetContent != null) {
            const contentDefaultDom = new DOMParser().parseFromString(
                targetDefaultContent,
                "text/xml"
            );
            const contentDom = new DOMParser().parseFromString(
                targetContent,
                "text/xml"
            );

            const selectXpath = xpath.useNamespaces({
                w: getNamespace(this.namespaces, "w"),
            });
            const nodeXpathQuery = "//w:style";
            const nodesXpath = selectXpath(
                nodeXpathQuery,
                contentDefaultDom
            ) as Element[];
            nodesXpath.forEach(nodeXpath => {
                // check if the MS Word style name already exists. Do not add styles with the same ID
                const newStyleId = nodeXpath.getAttribute("w:styleId");
                const nodesXpathStylesByName = selectXpath(
                    '//w:style[@w:styleId="' + newStyleId + '"]',
                    contentDom
                ) as Element[];
                if (nodesXpathStylesByName.length == 0) {
                    this.addXmlStyle(
                        new XMLSerializer().serializeToString(nodeXpath)
                    );
                }
            });
        }
    }

    /**
     * Inits DOCX structure to adjust contents if needed and extra settings
     *
     * @throws Error reading DOCX file
     */
    public initDocxStructure() {
        // get DOCX namespaces from content
        // default namespaces as transitional variant
        this.namespaces.set(
            "a",
            "http://schemas.openxmlformats.org/drawingml/2006/main"
        );
        this.namespaces.set(
            "m",
            "http://schemas.openxmlformats.org/officeDocument/2006/math"
        );
        this.namespaces.set(
            "r",
            "http://schemas.openxmlformats.org/package/2006/relationships"
        );
        this.namespaces.set(
            "rels",
            "http://schemas.openxmlformats.org/package/2006/relationships"
        );
        this.namespaces.set(
            "w",
            "http://schemas.openxmlformats.org/wordprocessingml/2006/main"
        );
        this.namespaces.set(
            "wp",
            "http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing"
        );

        const targetContentsPathDocument =
            this.getContentPathsByType("document");

        if (
            targetContentsPathDocument != null &&
            targetContentsPathDocument.length > 0
        ) {
            const targetDocumentKey = targetContentsPathDocument[0];
            let targetDocumentContent = this.getTextContent(targetDocumentKey);

            if (targetDocumentContent != null) {
                let contentDom = new DOMParser().parseFromString(
                    targetDocumentContent,
                    "text/xml"
                );
                const selectXpath = xpath.useNamespaces({
                    w: getNamespace(this.namespaces, "w"),
                });

                // check if there's a section. Otherwise add it before </w:body>
                const nodeXpathQuerySection = "//w:body/w:sectPr";
                const nodesXpathSection = selectXpath(
                    nodeXpathQuerySection,
                    contentDom
                ) as Element[];
                if (nodesXpathSection.length == 0) {
                    targetDocumentContent = targetDocumentContent.replace(
                        "</w:body>",
                        '<w:sectPr><w:pgSz w:w="11906" w:h="16838"/><w:pgMar w:top="1417" w:right="1701" w:bottom="1417" w:left="1701" w:header="708" w:footer="708" w:gutter="0"/><w:cols w:space="708"/><w:docGrid w:linePitch="360"/></w:sectPr></w:body>'
                    );
                    this.addTextContent(
                        targetDocumentKey,
                        targetDocumentContent
                    );

                    // refresh DOM contents to be used when checking the strict variant
                    contentDom = new DOMParser().parseFromString(
                        targetDocumentContent,
                        "text/xml"
                    );
                }

                // check if strict variant is used
                const selectXpathStrict = xpath.useNamespaces({
                    w: "http://purl.oclc.org/ooxml/wordprocessingml/main",
                });
                const nodeXpathQueryDocument = "//w:document";
                const nodesXpathDocument = selectXpathStrict(
                    nodeXpathQueryDocument,
                    contentDom
                ) as Element[];
                if (nodesXpathDocument.length > 0) {
                    const conformanceAttribute =
                        nodesXpathDocument[0].getAttribute("w:conformance");
                    if (
                        conformanceAttribute != null &&
                        conformanceAttribute == "strict"
                    ) {
                        // move strict variant namespaces to transitional
                        for (const [
                            filePath,
                            fileContent,
                        ] of this.textContents.entries()) {
                            const fileContentReplaced = fileContent
                                .replace(
                                    generateRegExpForReplacement(
                                        "http://purl.oclc.org/ooxml/officeDocument"
                                    ),
                                    "http://schemas.openxmlformats.org/officeDocument/2006"
                                )
                                .replace(
                                    generateRegExpForReplacement(
                                        "http://purl.oclc.org/ooxml/wordprocessingml"
                                    ),
                                    "http://schemas.openxmlformats.org/wordprocessingml/2006"
                                )
                                .replace(
                                    generateRegExpForReplacement(
                                        "http://purl.oclc.org/ooxml/drawingml"
                                    ),
                                    "http://schemas.openxmlformats.org/drawingml/2006"
                                );

                            this.addTextContent(filePath, fileContentReplaced);
                        }

                        this.conformance = "strict";
                    }
                }
            }
        } else {
            throw new Error("Error reading DOCX contents: invalid contents.");
        }
    }

    /**
     * Modifies sections
     *
     * @param options - Options section layout
     * @returns XML
     */
    public modifySections(options: OptionsSectionLayout) {
        const targetContentsPathDocument =
            this.getContentPathsByType("document");

        if (
            targetContentsPathDocument != null &&
            targetContentsPathDocument.length > 0
        ) {
            const targetDocumentKey = targetContentsPathDocument[0];
            const targetDocumentContent =
                this.getTextContent(targetDocumentKey);

            if (targetDocumentContent != null) {
                const contentDom = new DOMParser().parseFromString(
                    targetDocumentContent,
                    "text/xml"
                );
                const selectXpath = xpath.useNamespaces({
                    w: getNamespace(this.namespaces, "w"),
                });

                let nodeXpathQuerySection = "//w:sectPr";
                if (
                    options.onlyLastSection != null &&
                    options.onlyLastSection
                ) {
                    nodeXpathQuerySection = "(//w:sectPr)[last()]";
                }
                const nodesXpathSection = selectXpath(
                    nodeXpathQuerySection,
                    contentDom
                ) as Element[];

                if (nodesXpathSection.length > 0) {
                    if (options.size != null) {
                        // update options with preset sizes
                        const sectionElement = new CreateSection(options);
                        options = sectionElement.getOptions();
                    }

                    nodesXpathSection.forEach(nodeXpathSection => {
                        const nodePgSz =
                            nodeXpathSection.getElementsByTagNameNS(
                                getNamespace(this.namespaces, "w"),
                                "pgSz"
                            );
                        if (nodePgSz.length > 0) {
                            if (options.code != null) {
                                nodePgSz
                                    .item(0)
                                    ?.setAttribute(
                                        "w:code",
                                        options.code.toString()
                                    );
                            }
                            if (options.height != null) {
                                nodePgSz
                                    .item(0)
                                    ?.setAttribute(
                                        "w:h",
                                        options.height.toString()
                                    );
                            }
                            if (options.orient != null) {
                                nodePgSz
                                    .item(0)
                                    ?.setAttribute("w:orient", options.orient);
                            }
                            if (options.width != null) {
                                nodePgSz
                                    .item(0)
                                    ?.setAttribute(
                                        "w:w",
                                        options.width.toString()
                                    );
                            }
                        }
                        const nodePgMar =
                            nodeXpathSection.getElementsByTagNameNS(
                                getNamespace(this.namespaces, "w"),
                                "pgMar"
                            );
                        if (nodePgMar.length > 0) {
                            if (options.margin?.bottom != null) {
                                nodePgMar
                                    .item(0)
                                    ?.setAttribute(
                                        "w:bottom",
                                        options.margin?.bottom.toString()
                                    );
                            }
                            if (options.margin?.footer != null) {
                                nodePgMar
                                    .item(0)
                                    ?.setAttribute(
                                        "w:footer",
                                        options.margin?.footer.toString()
                                    );
                            }
                            if (options.margin?.header != null) {
                                nodePgMar
                                    .item(0)
                                    ?.setAttribute(
                                        "w:header",
                                        options.margin?.header.toString()
                                    );
                            }
                            if (options.margin?.left != null) {
                                nodePgMar
                                    .item(0)
                                    ?.setAttribute(
                                        "w:left",
                                        options.margin?.left.toString()
                                    );
                            }
                            if (options.margin?.right != null) {
                                nodePgMar
                                    .item(0)
                                    ?.setAttribute(
                                        "w:right",
                                        options.margin?.right.toString()
                                    );
                            }
                            if (options.margin?.top != null) {
                                nodePgMar
                                    .item(0)
                                    ?.setAttribute(
                                        "w:top",
                                        options.margin?.top.toString()
                                    );
                            }
                        }
                        if (options.bidi != null) {
                            const nodeBidi =
                                nodeXpathSection.getElementsByTagNameNS(
                                    getNamespace(this.namespaces, "w"),
                                    "bidi"
                                );
                            if (nodeBidi.length > 0) {
                                nodeBidi
                                    .item(0)
                                    ?.setAttribute(
                                        "w:val",
                                        options.bidi.toString()
                                    );
                            } else {
                                const newNodeBidi =
                                    contentDom.createElement("w:bidi");
                                newNodeBidi.setAttribute(
                                    "w:val",
                                    options.bidi.toString()
                                );
                                nodeXpathSection.appendChild(newNodeBidi);
                            }
                        }
                        if (options.numberColumns != null) {
                            const nodeCols =
                                nodeXpathSection.getElementsByTagNameNS(
                                    getNamespace(this.namespaces, "w"),
                                    "cols"
                                );
                            if (nodeCols.length > 0) {
                                nodeCols
                                    .item(0)
                                    ?.setAttribute(
                                        "w:num",
                                        options.numberColumns.toString()
                                    );
                            } else {
                                const newNodeCols =
                                    contentDom.createElement("w:cols");
                                newNodeCols.setAttribute(
                                    "w:num",
                                    options.numberColumns.toString()
                                );
                                nodeXpathSection.appendChild(newNodeCols);
                            }
                        }
                        if (options.rtlGutter != null) {
                            const nodeRtlGutter =
                                nodeXpathSection.getElementsByTagNameNS(
                                    getNamespace(this.namespaces, "w"),
                                    "rtlGutter"
                                );
                            if (nodeRtlGutter.length > 0) {
                                nodeRtlGutter
                                    .item(0)
                                    ?.setAttribute(
                                        "w:val",
                                        options.rtlGutter.toString()
                                    );
                            } else {
                                const newNodeRtlGutter =
                                    contentDom.createElement("w:rtlGutter");
                                newNodeRtlGutter.setAttribute(
                                    "w:val",
                                    options.rtlGutter.toString()
                                );
                                nodeXpathSection.appendChild(newNodeRtlGutter);
                            }
                        }
                        if (options.type != null) {
                            const nodeType =
                                nodeXpathSection.getElementsByTagNameNS(
                                    getNamespace(this.namespaces, "w"),
                                    "type"
                                );

                            let addType = true;
                            if (nodeType.length > 0) {
                                if (
                                    nodeType.item(0)?.hasAttribute("w:val") &&
                                    nodeType.item(0)?.getAttribute("w:val") ==
                                        options.type
                                ) {
                                    addType = false;
                                }
                            }

                            if (addType) {
                                const newNodeType =
                                    contentDom.createElement("w:type");
                                newNodeType.setAttribute("w:val", options.type);
                                const nodePgSzParent =
                                    nodePgSz.item(0)?.parentNode;
                                if (nodePgSzParent != null) {
                                    nodePgSzParent.insertBefore(
                                        newNodeType,
                                        nodePgSz.item(0)
                                    );
                                }
                            }
                        }

                        if (
                            options.excludeHeadersFooters != null &&
                            options.excludeHeadersFooters
                        ) {
                            // remove headers and footers from the cloned section
                            const nodesHeaderReference =
                                nodeXpathSection.getElementsByTagNameNS(
                                    getNamespace(this.namespaces, "w"),
                                    "headerReference"
                                );
                            Array.from(nodesHeaderReference).forEach(
                                nodeHeaderReference => {
                                    nodeHeaderReference.parentNode?.removeChild(
                                        nodeHeaderReference
                                    );
                                }
                            );
                            const nodesFooterReference =
                                nodeXpathSection.getElementsByTagNameNS(
                                    getNamespace(this.namespaces, "w"),
                                    "footerReference"
                                );
                            Array.from(nodesFooterReference).forEach(
                                nodeFooterReference => {
                                    nodeFooterReference.parentNode?.removeChild(
                                        nodeFooterReference
                                    );
                                }
                            );
                        }
                    });

                    this.addTextContent(
                        targetDocumentKey,
                        new XMLSerializer().serializeToString(contentDom)
                    );
                }
            }
        }
    }

    /**
     * Removes an XML element using an XPath query
     *
     * @param xml - XML content
     * @param query - XPath query
     * @returns XML
     */
    public removeXmlElement(xml: string, query: string): string {
        const xmlDom = new DOMParser().parseFromString(xml, "text/xml");
        const selectXpath = xpath.useNamespaces({
            w: getNamespace(this.namespaces, "w"),
            wp: getNamespace(this.namespaces, "wp"),
        });
        const nodesXpath = selectXpath(query, xmlDom) as Element[];

        // iterate nodes to remove them
        nodesXpath.forEach(nodeXpath => {
            const nodeParent = nodeXpath.parentNode as Element;
            if (nodeParent != null) {
                // if a paragraph is removed inside a table cell check it's not empty
                if (nodeParent.nodeName == "w:tc") {
                    const nodesPInTc = nodeParent.getElementsByTagNameNS(
                        getNamespace(this.namespaces, "w"),
                        "p"
                    );
                    if (nodesPInTc.length == 1) {
                        const newNodeP = xmlDom.createElement("w:p");
                        nodeParent.appendChild(newNodeP);
                    }
                    nodeParent.removeChild(nodeXpath);
                } else {
                    nodeParent.removeChild(nodeXpath);
                }
            }
        });

        return new XMLSerializer().serializeToString(xmlDom);
    }

    /**
     * Repairs XML contents wrapped by template symbols removing extra tags
     *
     * @param content - XML content
     * @param templateSymbolStart - Template symbol start
     * @param templateSymbolEnd - Template symbol end
     * @returns XML repaired
     */
    public repairXmlVariables(
        content: string,
        templateSymbolStart: string,
        templateSymbolEnd: string
    ): string {
        let templateSymbol = [...(templateSymbolStart + templateSymbolEnd)];

        // get XPath to query paragraphs that contain placeholder symbols
        let templateSymbolXPathQuery =
            '//w:p[contains(., "' + templateSymbol[0] + '")';
        templateSymbol.slice(1).forEach(function (symbolValue) {
            templateSymbolXPathQuery +=
                ' and contains(., "' + symbolValue + '")';
        });
        templateSymbolXPathQuery += "]";

        const contentDom = new DOMParser().parseFromString(content, "text/xml");
        const selectXpath = xpath.useNamespaces({
            w: getNamespace(this.namespaces, "w"),
        });
        const nodesXpath = selectXpath(
            templateSymbolXPathQuery,
            contentDom
        ) as Element[];

        // iterate nodes to repair them
        nodesXpath.forEach(nodeXpath => {
            templateSymbol = [...(templateSymbolStart + templateSymbolEnd)];
            const templateSymbolQuoted = templateSymbol.map(symbol =>
                escapeRegExp(symbol)
            );
            const templateSymbolXpathRegExpr =
                templateSymbolQuoted.join("(.*?)");
            const matchesPlaceholders = nodeXpath
                .toString()
                .match(new RegExp(templateSymbolXpathRegExpr, "g"));
            if (matchesPlaceholders != null) {
                matchesPlaceholders.forEach(matchPlaceholders => {
                    const mathPlaceholderNoTag = matchPlaceholders.replace(
                        /<[^>]+>/g,
                        ""
                    );

                    content = content.replace(
                        generateRegExpForReplacement(matchPlaceholders),
                        mathPlaceholderNoTag
                    );
                });
            }
        });

        return content;
    }

    /**
     * Replaces XML image
     *
     * @param xml - XML content
     * @param query - XPath query
     * @param image - Image
     * @param imageId - Image Id
     * @param options - Replace variable image options
     * @returns XML
     */
    public replaceXmlImage(
        xml: string,
        query: string,
        elementImage: CreateImage,
        imageId: string,
        options: OptionsReplaceVariableImage
    ): [string, number] {
        const xmlDom = new DOMParser().parseFromString(xml, "text/xml");
        const selectXpath = xpath.useNamespaces({
            w: getNamespace(this.namespaces, "w"),
            wp: getNamespace(this.namespaces, "wp"),
        });
        const nodesXpath = selectXpath(query, xmlDom) as Element[];

        // iterate nodes to replace them
        nodesXpath.forEach(nodeXpath => {
            const nodeParent = nodeXpath.parentNode as Element;
            if (nodeParent != null) {
                const nodeBlip = nodeParent.getElementsByTagNameNS(
                    getNamespace(this.namespaces, "a"),
                    "blip"
                );
                if (nodeBlip.length > 0) {
                    nodeBlip.item(0)?.setAttribute("r:embed", "rId" + imageId);
                }
                let width: number | undefined;
                let height: number | undefined;
                if (options.width != null) {
                    width = options.width;
                } else {
                    width = elementImage.getOptions().width;
                }
                if (options.height != null) {
                    height = options.height;
                } else {
                    height = elementImage.getOptions().height;
                }
                if (width != null && width != -1) {
                    const nodeExtent = nodeParent.getElementsByTagNameNS(
                        getNamespace(this.namespaces, "wp"),
                        "extent"
                    );
                    nodeExtent.item(0)?.setAttribute("cx", width.toString());
                    const nodeXfrm = nodeParent.getElementsByTagNameNS(
                        getNamespace(this.namespaces, "a"),
                        "xfrm"
                    );
                    if (nodeXfrm.length > 0) {
                        const nodeExt = nodeXfrm
                            .item(0)
                            ?.getElementsByTagNameNS(
                                getNamespace(this.namespaces, "a"),
                                "ext"
                            );
                        nodeExt?.item(0)?.setAttribute("cx", width.toString());
                    }
                }
                if (height != null && height != -1) {
                    const nodeExtent = nodeParent.getElementsByTagNameNS(
                        getNamespace(this.namespaces, "wp"),
                        "extent"
                    );
                    nodeExtent.item(0)?.setAttribute("cy", height.toString());
                    const nodeXfrm = nodeParent.getElementsByTagNameNS(
                        getNamespace(this.namespaces, "a"),
                        "xfrm"
                    );
                    if (nodeXfrm.length > 0) {
                        const nodeExt = nodeXfrm
                            .item(0)
                            ?.getElementsByTagNameNS(
                                getNamespace(this.namespaces, "a"),
                                "ext"
                            );
                        nodeExt?.item(0)?.setAttribute("cy", height.toString());
                    }
                }

                if (options.firstMatch) {
                    nodeXpath.setAttribute("descr", "");
                    nodeXpath.setAttribute("title", "");
                }

                const docPrId = generateRandomNumber(99999999, 999999999, []);
                nodeXpath.setAttribute("id", docPrId.toString());
            }
        });

        return [
            new XMLSerializer().serializeToString(xmlDom),
            nodesXpath.length,
        ];
    }

    /**
     * Replaces XML list
     *
     * @param xml - XML content
     * @param variable - Variable name
     * @param query - XPath query
     * @param items - List items
     * @param options - Options replace list
     * @param templateSymbolStart - Template symbol start
     * @param templateSymbolEnd - Template symbol end
     * @returns XML and WordFragments
     */
    public replaceXmlList(
        xml: string,
        variable: string,
        query: string,
        items: ListNested[],
        options: OptionsReplaceVariableList,
        templateSymbolStart: string,
        templateSymbolEnd: string
    ): [string, Map<string, WordFragment>] {
        const xmlDom = new DOMParser().parseFromString(xml, "text/xml");
        const selectXpath = xpath.useNamespaces({
            w: getNamespace(this.namespaces, "w"),
        });
        const nodesXpath = selectXpath(query, xmlDom) as Element[];
        let wordFragmentValues = new Map<string, WordFragment>();

        // iterate nodes to replace them and fill the list
        nodesXpath.forEach(nodeXpath => {
            const nodeIlvl = nodeXpath.getElementsByTagNameNS(
                getNamespace(this.namespaces, "w"),
                "ilvl"
            );
            const levelInitList =
                Number(nodeIlvl.item(0)?.getAttribute("w:val")) ?? 0;
            const wordFragments = this.replaceListValues(
                variable,
                items,
                nodeXpath,
                options,
                levelInitList,
                templateSymbolStart,
                templateSymbolEnd
            );
            wordFragmentValues = new Map([
                ...wordFragmentValues,
                ...wordFragments,
            ]);

            nodeXpath.parentNode?.removeChild(nodeXpath);
        });

        let xmlContents = new XMLSerializer().serializeToString(xmlDom);

        // replace line breaks
        if (options.parseLineBreaks) {
            xmlContents = xmlContents.replace(
                generateRegExpForReplacement("TKNPDXLINEBREAKINT"),
                '</w:t><w:br/><w:t xml:space="preserve">'
            );
        }

        return [xmlContents, wordFragmentValues];
    }

    /**
     * Replaces XML table
     *
     * @param xml - XML content
     * @param variables - Variables
     * @param query - XPath query
     * @param options - Options replace table
     * @param templateSymbolStart - Template symbol start
     * @param templateSymbolEnd - Template symbol end
     * @returns XML and WordFragments
     */
    public replaceXmlTable(
        xml: string,
        variables: Map<string, string | WordFragment>[],
        query: string,
        options: OptionsReplaceVariableTable,
        templateSymbolStart: string,
        templateSymbolEnd: string
    ): [string, Map<string, WordFragment>] {
        const xmlDom = new DOMParser().parseFromString(xml, "text/xml");
        const selectXpath = xpath.useNamespaces({
            w: getNamespace(this.namespaces, "w"),
        });
        const nodesXpath = selectXpath(query, xmlDom) as Element[];
        const wordFragmentValues = new Map<string, WordFragment>();
        // iterate nodes to replace them and fill the list
        nodesXpath.forEach(nodeXpath => {
            variables.forEach(variablesRow => {
                const newNode = nodeXpath.cloneNode(true) as Element;
                for (let [
                    attributeName,
                    attributeValue,
                ] of variablesRow.entries()) {
                    attributeName = applyHtmlSpecialChars(attributeName);
                    const newNodeTNodes = newNode.getElementsByTagNameNS(
                        getNamespace(this.namespaces, "w"),
                        "t"
                    );
                    Array.from(newNodeTNodes).forEach(newNodeT => {
                        if (typeof attributeValue === "string") {
                            if (options.parseLineBreaks) {
                                attributeValue = attributeValue.replace(
                                    /\n\r|\n|\r\n|\r/g,
                                    "TKNPDXLINEBREAKINT"
                                );
                            }

                            newNodeT.textContent = newNodeT.textContent
                                ? newNodeT.textContent.replace(
                                      generateRegExpForReplacement(
                                          templateSymbolStart +
                                              applyHtmlSpecialChars(
                                                  attributeName
                                              ) +
                                              templateSymbolStart
                                      ),
                                      attributeValue
                                  )
                                : applyHtmlSpecialChars(attributeValue);
                        } else if (attributeValue instanceof WordFragment) {
                            // do not replace WordFragments directly. Keep them to use replaceVariableWordFragment to replace them
                            const keyWordFragment = generateUniqid(
                                "TKNPDXWORDFRAGMENTINT"
                            );
                            newNodeT.textContent = newNodeT.textContent
                                ? newNodeT.textContent.replace(
                                      generateRegExpForReplacement(
                                          templateSymbolStart +
                                              applyHtmlSpecialChars(
                                                  attributeName
                                              ) +
                                              templateSymbolStart
                                      ),
                                      templateSymbolStart +
                                          keyWordFragment +
                                          templateSymbolEnd
                                  )
                                : keyWordFragment;

                            wordFragmentValues.set(
                                keyWordFragment,
                                attributeValue
                            );
                        }
                    });

                    nodeXpath.parentNode?.insertBefore(newNode, nodeXpath);
                }
            });
            nodeXpath.parentNode?.removeChild(nodeXpath);
        });

        let xmlContents = new XMLSerializer().serializeToString(xmlDom);

        // replace line breaks
        if (options.parseLineBreaks) {
            xmlContents = xmlContents.replace(
                generateRegExpForReplacement("TKNPDXLINEBREAKINT"),
                '</w:t><w:br/><w:t xml:space="preserve">'
            );
        }

        return [xmlContents, wordFragmentValues];
    }

    /**
     * Replaces an XML element with WordFragment using an XPath query
     *
     * @param xml - XML content
     * @param query - XPath query
     * @param variable - Variable name
     * @param variableValue - Variable value
     * @param options - Options replace text with WordFragment
     * @returns XML
     */
    public replaceXmlWordFragment(
        xml: string,
        query: string,
        variable: string,
        variableValue: WordFragment,
        options: OptionsReplaceVariableWordFragment
    ): string {
        const xmlDom = new DOMParser().parseFromString(xml, "text/xml");
        const selectXpath = xpath.useNamespaces({
            w: getNamespace(this.namespaces, "w"),
        });
        const nodesXpath = selectXpath(query, xmlDom) as Element[];

        // iterate nodes to replace them
        nodesXpath.forEach(nodeXpath => {
            const nodeParent = nodeXpath.parentNode as Element;
            if (options.type == "inline") {
                // block inline replacement

                // get the w:r tag that contains the variable
                const nodesR = selectXpath(
                    '//w:r/w:t[text()[contains(.,"' + variable + '")]]',
                    nodeXpath
                ) as Element[];
                if (nodesR != null && nodesR.length > 0) {
                    // get existing w:rPr styles to be applied to the content after the new element and keep styles
                    let newRContent = "<w:r>";
                    const nodesRpR = nodesR[0].getElementsByTagNameNS(
                        getNamespace(this.namespaces, "w"),
                        "rPr"
                    );
                    if (nodesRpR.length > 0) {
                        const nodeRpr = nodesRpR.item(0);
                        newRContent += nodeRpr
                            ? new XMLSerializer().serializeToString(nodeRpr)
                            : "";
                    }

                    // generate a new paragraph with the variable replaced
                    const nodeRContent =
                        "</w:t></w:r>" +
                        variableValue
                            .getDocxContent()
                            .getInlineXmlContents(variableValue.toString()) +
                        newRContent +
                        '<w:t xml:space="preserve">';
                    let newPContent = new XMLSerializer().serializeToString(
                        nodeXpath
                    );
                    newPContent = newPContent.replace(
                        generateRegExpForReplacement(
                            applyHtmlSpecialChars(variable)
                        ),
                        nodeRContent
                    );
                    // apply space preserve to all w:t tags to keep blank spaces
                    newPContent = newPContent.replace(
                        generateRegExpForReplacement("<w:t>"),
                        '<w:t xml:space="preserve">'
                    );

                    // add the new paragraph
                    const xmlDomContentP = new DOMParser().parseFromString(
                        newPContent,
                        "text/xml"
                    );
                    nodeParent.insertBefore(
                        xmlDomContentP.documentElement,
                        nodeXpath
                    );

                    // remove the paragraph that contains the variable
                    nodeParent.removeChild(nodeXpath);
                }
            } else if (options.type == "block") {
                // block type replacement

                // wrap the XML contents in a w:root tag to add all child nodes
                const xmlDom = new DOMParser().parseFromString(
                    "<w:root>" + variableValue + "</w:root>",
                    "text/xml"
                );
                const childNodes = xmlDom.documentElement.childNodes;
                Array.from(childNodes).forEach(childNode => {
                    // clone the node to be inserted to avoid removing it from childNodes
                    const newChildNode = childNode.cloneNode(true);
                    nodeParent.insertBefore(newChildNode, nodeXpath);
                });
                // remove the paragraph that contains the variable
                nodeParent.removeChild(nodeXpath);
            }
        });

        return new XMLSerializer().serializeToString(xmlDom);
    }

    /**
     * Replaces list node by a new list
     *
     * @param variable - Variable name
     * @param items - List items
     * @param domNode - Node that contains the list
     * @param options - Options replace list
     * @param level - Depth level
     * @param templateSymbolStart - Template symbol start
     * @param templateSymbolEnd - Template symbol end
     * @returns WordFragments
     */
    protected replaceListValues(
        variable: string,
        items: ListNested[],
        domNode: Element,
        options: OptionsReplaceVariableList,
        level: number,
        templateSymbolStart: string,
        templateSymbolEnd: string
    ): Map<string, WordFragment> {
        let wordFragmentValues = new Map<string, WordFragment>();

        items.forEach(item => {
            if (typeof item === "string" || item instanceof WordFragment) {
                const newNode = domNode.cloneNode(true) as Element;
                const nodeIlvl = newNode.getElementsByTagNameNS(
                    getNamespace(this.namespaces, "w"),
                    "ilvl"
                );
                nodeIlvl.item(0)?.setAttribute("w:val", level.toString());
                const newNodeTNodes = newNode.getElementsByTagNameNS(
                    getNamespace(this.namespaces, "w"),
                    "t"
                );
                Array.from(newNodeTNodes).forEach(newNodeT => {
                    if (options.parseLineBreaks && typeof item === "string") {
                        item = item.replace(
                            /\n\r|\n|\r\n|\r/g,
                            "TKNPDXLINEBREAKINT"
                        );
                    }

                    if (typeof item === "string") {
                        newNodeT.textContent = newNodeT.textContent
                            ? newNodeT.textContent.replace(
                                  generateRegExpForReplacement(
                                      applyHtmlSpecialChars(variable)
                                  ),
                                  item
                              )
                            : item;
                    } else if (item instanceof WordFragment) {
                        // do not replace WordFragments directly. Keep them to use replaceVariableWordFragment to replace them
                        const keyWordFragment = generateUniqid(
                            "TKNPDXWORDFRAGMENTINT"
                        );
                        newNodeT.textContent = newNodeT.textContent
                            ? newNodeT.textContent.replace(
                                  generateRegExpForReplacement(
                                      applyHtmlSpecialChars(variable)
                                  ),
                                  templateSymbolStart +
                                      keyWordFragment +
                                      templateSymbolEnd
                              )
                            : keyWordFragment;

                        wordFragmentValues.set(keyWordFragment, item);
                    }
                });
                const nodeParent = domNode.parentNode as Element;
                nodeParent?.insertBefore(newNode, domNode);
            } else if (Array.isArray(item)) {
                // a subarray, iterate it to add the contents
                const listWordFragments = this.replaceListValues(
                    variable,
                    item,
                    domNode,
                    options,
                    level + 1,
                    templateSymbolStart,
                    templateSymbolEnd
                );
                wordFragmentValues = new Map([
                    ...wordFragmentValues,
                    ...listWordFragments,
                ]);
            }
        });

        return wordFragmentValues;
    }
}

export { ContentType, Docx };
